import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { BRANDS, APPLICATIONS, PROJECT_TYPES } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";
import { SITE } from "@/config/site";
import { sendForm } from "@/lib/sendForm";
import GdprLabel from "@/components/GdprLabel";
import FormError from "@/components/FormError";
import { translate } from "@/i18n";

const inputCls = "w-full bg-[#121316] border border-white/10 px-4 py-3 text-sm text-white font-light focus:border-[#C2A675] focus:outline-none transition-colors";
const labelCls = "block text-[11px] uppercase tracking-[0.16em] text-[#A8A6A0] mb-2";
const optionCls = (active) =>
  `px-4 py-3 border text-xs uppercase tracking-[0.14em] text-left transition-colors ${
    active ? "border-[#C2A675] text-[#C2A675] bg-[#C2A675]/5" : "border-white/15 text-[#A8A6A0] hover:border-white/40 hover:text-white"
  }`;

export default function Cotacao() {
  const { t, lang } = useLanguage();
  const [params] = useSearchParams();
  const initialMarca = BRANDS.some((b) => b.slug === params.get("marca")) ? params.get("marca") : null;

  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    marca: initialMarca, aplicacao: null, tipo_projeto: "", quantidade: "", localizacao: "", mensagem: "",
    nome: "", empresa: "", email: "", telefone: "", consentimento: false, website: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const startedAt = useRef(Date.now());

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const steps = t("quote.steps");

  const canNext = useMemo(() => {
    if (step === 0) return !!data.marca;
    if (step === 1) return !!data.aplicacao;
    if (step === 2) return !!data.tipo_projeto;
    if (step === 3) return data.nome.length >= 2 && /.+@.+\..+/.test(data.email) && data.consentimento;
    return true;
  }, [step, data]);

  const submit = async () => {
    setSending(true);
    setError(false);
    try {
      await sendForm({
        form: "orcamento",
        lang,
        honeypot: data.website,
        startedAt: startedAt.current,
        fields: {
          marca: BRANDS.find((b) => b.slug === data.marca)?.name,
          aplicacao: translate("pt", `app_items.${data.aplicacao}`),
          tipo_projeto: translate("pt", `quote.tipos.${data.tipo_projeto}`),
          quantidade: data.quantidade,
          localizacao: data.localizacao,
          mensagem: data.mensagem,
          nome: data.nome, empresa: data.empresa, email: data.email, telefone: data.telefone,
        },
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setError(true); // os dados e o passo atual mantêm-se
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="pt-20 min-h-[70vh] flex items-center">
        <Seo title={t("seo.quote.title")} description={t("seo.quote.desc")} />
        <div className="max-w-2xl mx-auto px-6 py-24 text-left">
          <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("quote.tag")}</span>
          <h1 className="serif-display text-4xl sm:text-5xl font-light text-white mt-4 mb-6">{t("form.success_title")}</h1>
          <p className="text-sm text-[#A8A6A0] font-light leading-relaxed mb-10">{t("form.success_text")}</p>
          <button
            type="button"
            data-testid="quote-new-button"
            onClick={() => { setDone(false); setStep(0); startedAt.current = Date.now(); setData({ marca: null, aplicacao: null, tipo_projeto: "", quantidade: "", localizacao: "", mensagem: "", nome: "", empresa: "", email: "", telefone: "", consentimento: false, website: "" }); }}
            className="px-8 py-4 border border-[#C2A675]/60 text-[#C2A675] text-xs uppercase tracking-[0.18em] hover:bg-[#C2A675] hover:text-[#121316] transition-colors"
          >
            {t("quote.new")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page" className="pt-20">
      <Seo title={t("seo.quote.title")} description={t("seo.quote.desc")} />

      <section className="pt-16 lg:pt-20 pb-20 max-w-4xl mx-auto px-6">
        <span className="mono-tag text-xs uppercase tracking-[0.22em] text-[#C2A675]">{t("quote.tag")}</span>
        <h1 className="serif-display text-4xl sm:text-5xl font-light text-white mt-4 mb-4">{t("quote.title")}</h1>
        <p className="text-base text-[#A8A6A0] font-light mb-10">{t("quote.sub")}</p>

        {/* Progress */}
        <div className="flex items-center gap-0 mb-10" data-testid="quote-progress" aria-label={`${t("common.step")} ${step + 1} ${t("common.of")} ${steps.length}`}>
          {steps.map((s, i) => (
            <div key={s} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-start gap-2">
                <span className={`w-7 h-7 flex items-center justify-center border text-[11px] transition-colors ${i < step ? "bg-[#C2A675] border-[#C2A675] text-[#121316]" : i === step ? "border-[#C2A675] text-[#C2A675]" : "border-white/15 text-[#A8A6A0]"}`}>
                  {i < step ? <Check size={13} aria-hidden="true" /> : i + 1}
                </span>
                <span className={`hidden sm:block text-[10px] uppercase tracking-[0.14em] ${i === step ? "text-[#C2A675]" : "text-[#A8A6A0]"}`}>{s}</span>
              </div>
              {i < steps.length - 1 && <span className={`h-px flex-1 mx-3 mb-0 sm:mb-6 ${i < step ? "bg-[#C2A675]" : "bg-white/10"}`} aria-hidden="true" />}
            </div>
          ))}
        </div>

        <div className="bg-[#17191E] border border-white/10 p-8 md:p-10">
          {step === 0 && (
            <div data-testid="quote-step-brand">
              <h2 className="serif-display text-2xl font-light text-white mb-8">{t("quote.s1_title")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {BRANDS.map((b) => (
                  <button key={b.slug} type="button" onClick={() => set("marca", b.slug)} data-testid={`quote-brand-${b.slug}`} className={optionCls(data.marca === b.slug)}>
                    <span className="serif-display text-lg normal-case tracking-normal block text-white font-light">{b.name}</span>
                    <span className="block mt-1">{t(`brand.${b.slug}.tag`)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="quote-step-application">
              <h2 className="serif-display text-2xl font-light text-white mb-8">{t("quote.s2_title")}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {APPLICATIONS.map((a) => (
                  <button key={a.id} type="button" onClick={() => set("aplicacao", a.id)} data-testid={`quote-app-${a.id}`} className={optionCls(data.aplicacao === a.id)}>
                    {t(`app_items.${a.id}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="quote-step-project" className="space-y-6">
              <h2 className="serif-display text-2xl font-light text-white">{t("quote.s3_title")}</h2>
              <div>
                <label htmlFor="q-tipo" className={labelCls}>{t("quote.tipo")} *</label>
                <select id="q-tipo" data-testid="quote-type-select" value={data.tipo_projeto} onChange={(e) => set("tipo_projeto", e.target.value)} className={inputCls}>
                  <option value="">—</option>
                  {PROJECT_TYPES.map((p) => <option key={p} value={p}>{t(`quote.tipos.${p}`)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="q-quant" className={labelCls}>{t("quote.quantidade")}</label>
                  <input id="q-quant" data-testid="quote-quantity-input" type="text" value={data.quantidade} onChange={(e) => set("quantidade", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="q-loc" className={labelCls}>{t("quote.localizacao")}</label>
                  <input id="q-loc" data-testid="quote-location-input" type="text" value={data.localizacao} onChange={(e) => set("localizacao", e.target.value)} className={inputCls} />
                </div>
              </div>
              <div>
                <label htmlFor="q-msg" className={labelCls}>{t("form.mensagem")}</label>
                <textarea id="q-msg" data-testid="quote-message-input" rows={3} value={data.mensagem} onChange={(e) => set("mensagem", e.target.value)} className={inputCls} />
              </div>
              <p className="text-xs text-[#A8A6A0] font-light leading-relaxed">
                {t("quote.files_hint")} <a href={`mailto:${SITE.email}`} className="text-[#E8E6E1] underline underline-offset-4 hover:text-[#C2A675] transition-colors">{SITE.email}</a>.
              </p>
            </div>
          )}

          {step === 3 && (
            <div data-testid="quote-step-contact" className="space-y-6">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={data.website} onChange={(e) => set("website", e.target.value)} className="hidden" />
              <h2 className="serif-display text-2xl font-light text-white">{t("quote.s4_title")}</h2>
              <div className="border border-white/10 p-5 text-xs text-[#A8A6A0] font-light space-y-1.5" data-testid="quote-review">
                <p className="mono-tag text-[10px] uppercase tracking-[0.2em] text-[#C2A675] mb-2">{t("quote.review")}</p>
                <p>{t("common.brand")}: <span className="text-white">{BRANDS.find((b) => b.slug === data.marca)?.name}</span></p>
                <p>{t("explorer.filter_app")}: <span className="text-white">{t(`app_items.${data.aplicacao}`)}</span></p>
                <p>{t("quote.tipo")}: <span className="text-white">{t(`quote.tipos.${data.tipo_projeto}`)}</span></p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="q-nome" className={labelCls}>{t("form.nome")} *</label>
                  <input id="q-nome" data-testid="quote-name-input" type="text" required value={data.nome} onChange={(e) => set("nome", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="q-empresa" className={labelCls}>{t("form.empresa")}</label>
                  <input id="q-empresa" data-testid="quote-company-input" type="text" value={data.empresa} onChange={(e) => set("empresa", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="q-email" className={labelCls}>{t("form.email")} *</label>
                  <input id="q-email" data-testid="quote-email-input" type="email" required value={data.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="q-tel" className={labelCls}>{t("form.telefone")}</label>
                  <input id="q-tel" data-testid="quote-phone-input" type="tel" value={data.telefone} onChange={(e) => set("telefone", e.target.value)} className={inputCls} />
                </div>
              </div>
              <div className="flex items-start gap-3">
                <input id="q-gdpr" data-testid="quote-gdpr-checkbox" type="checkbox" checked={data.consentimento} onChange={(e) => set("consentimento", e.target.checked)} className="mt-1 accent-[#C2A675]" />
                <GdprLabel htmlFor="q-gdpr" />
              </div>
            </div>
          )}

          {error && <div className="mt-8"><FormError /></div>}
          <div className={`flex items-center justify-between ${error ? "mt-6" : "mt-12"} pt-8 border-t border-white/10`}>
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="quote-back-button"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#A8A6A0] hover:text-white transition-colors disabled:opacity-30"
            >
              <ArrowLeft size={14} aria-hidden="true" /> {t("common.back")}
            </button>
            {step < steps.length - 1 ? (
              <button
                type="button"
                onClick={() => canNext && setStep((s) => s + 1)}
                disabled={!canNext}
                data-testid="quote-next-button"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C2A675] text-[#121316] font-medium text-xs uppercase tracking-[0.18em] hover:bg-white transition-colors disabled:opacity-40"
              >
                {t("common.next")} <ArrowRight size={14} aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={!canNext || sending}
                data-testid="quote-submit-button"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#C2A675] text-[#121316] font-medium text-xs uppercase tracking-[0.18em] hover:bg-white transition-colors disabled:opacity-40"
              >
                {sending ? t("common.sending") : t("common.submit")} <ArrowRight size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
